import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

import Loading from "../../components/common/loading";
import { AppCard, AppPage, AppPageHeader } from "../../components/common/appShell";
import NotificationManager from "../../utils/notificationManager";
import {
  summarizeDestinations,
  summarizeEventSelections,
  summarizeTargets,
} from "../../utils/notificationRules";

import "./notifications.css";

function authConfig() {
  return {
    headers: {
      Authorization: "Bearer " + localStorage.getItem("token"),
    },
  };
}

function statusClass(enabled) {
  return enabled
    ? "notifications-status notifications-status--enabled"
    : "notifications-status notifications-status--disabled";
}

export default function NotificationRulesPage() {
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const rulesResponse = await axios.get("/v1/notification-rules", authConfig());
        if (cancelled) {
          return;
        }
        setRules(Array.isArray(rulesResponse?.data?.data) ? rulesResponse.data.data : []);
      } catch (error) {
        if (!cancelled) {
          NotificationManager.alert("Unable to load notification rules.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const metrics = useMemo(() => {
    const enabledCount = rules.filter((rule) => rule.enabled).length;
    const totalDestinations = rules.reduce(
      (sum, rule) => sum + (Array.isArray(rule.destinations) ? rule.destinations.length : 0),
      0,
    );
    return {
      totalRules: rules.length,
      enabledCount,
      totalDestinations,
    };
  }, [rules]);

  if (loading) {
    return <Loading black />;
  }

  return (
    <AppPage className="notifications-page">
      <AppPageHeader
        // eyebrow="Team"
        title="Notifications"
        description="Manage cloud-side email and webhook rules for enterprise event alerts."
        actions={
          <>
            <Link
              to="/team/notification-deliveries"
              className="button secondary notifications-button-link"
            >
              Delivery History
            </Link>
            <Link
              to="/team/notifications/new"
              className="button notifications-button-link"
            >
              New Rule
            </Link>
          </>
        }
      />

      <div className="app-kpi-grid">
        <div className="app-kpi-card">
          <div className="app-kpi-card__value">{metrics.totalRules}</div>
          <div className="app-kpi-card__label">Rules</div>
          <div className="app-kpi-card__accent" />
        </div>
        <div className="app-kpi-card">
          <div className="app-kpi-card__value">{metrics.enabledCount}</div>
          <div className="app-kpi-card__label">Enabled Rules</div>
          <div className="app-kpi-card__accent" />
        </div>
        <div className="app-kpi-card">
          <div className="app-kpi-card__value">{metrics.totalDestinations}</div>
          <div className="app-kpi-card__label">Destinations</div>
          <div className="app-kpi-card__accent" />
        </div>
      </div>

      <AppCard flush>
        {rules.length === 0 ? (
          <div className="app-empty-state">
            No notification rules yet.
          </div>
        ) : (
          <div className="notifications-table-wrap">
            <table className="notifications-table">
              <thead>
                <tr>
                  <th>Rule</th>
                  <th>Status</th>
                  <th>Targets</th>
                  <th>Events</th>
                  <th>Destinations</th>
                  <th>Last Delivery</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => (
                  <tr key={rule.id}>
                    <td>
                      <div style={{ fontWeight: 700 }}>{rule.name}</div>
                      <div className="notifications-muted">
                        Sender: {rule.senderDisplayName || "Synaccess Cloud"}
                      </div>
                    </td>
                    <td>
                      <span className={statusClass(rule.enabled)}>
                        {rule.enabled ? "Enabled" : "Disabled"}
                      </span>
                    </td>
                    <td>{summarizeTargets(rule)}</td>
                    <td>{summarizeEventSelections(rule)}</td>
                    <td>{summarizeDestinations(rule)}</td>
                    <td>
                      {rule.lastDispatch ? (
                        <>
                          <span
                            className={`notifications-status notifications-status--${String(
                              rule.lastDispatch.status || "pending",
                            ).toLowerCase()}`}
                          >
                            {rule.lastDispatch.status}
                          </span>
                          <div className="notifications-muted" style={{ marginTop: 8 }}>
                            {new Date(rule.lastDispatch.createdAt).toLocaleString()}
                          </div>
                        </>
                      ) : (
                        <span className="notifications-muted">No deliveries yet</span>
                      )}
                    </td>
                    <td>
                      <div className="notifications-row-actions">
                        <Link
                          to={`/team/notifications/${rule.id}/edit`}
                          className="button secondary notifications-compact-button"
                        >
                          Edit
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AppCard>
    </AppPage>
  );
}
